import type { AgentSubscriber } from '@ag-ui/client';
import { HttpAgent } from '@ag-ui/client';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { ChatMessage } from '~/modules/music-iq/pages/chatbot-page/types';

// Music IQ agent (AG-UI) – streaming via /api/music_iq_agent
const MUSIC_IQ_AGENT_URL = '/api/music_iq_agent/';

// Time in ms to wait before showing "thinking" indicator when no chunks arrive.
const THINKING_TIMEOUT_MS = 1000;

export function useMusicIqAgent() {
  const { t } = useTranslation('data', { keyPrefix: 'musicIq' });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const thinkingTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );

  // We need two separate states here for tracking while a question is in flight.
  //
  // isThinking can toggle on/off several times while the back end is streaming
  // us responses to the question. We set isThinking on a timer because we always
  // want the user to know the back end is "thinking" while waiting for the next
  // set of response streams.
  //
  // isProcessingQuestion toggles only once for the entire question interaction
  // with the back end. So it goes to true when the user asks a question and then
  // is set to false either when the user aborts while waiting or the back end
  // tells us the stream is complete.
  //
  // isProcessingQuestion is used in the <ChatbotPrompter /> to disable all controls
  // while the question is in flight. If we used isThinking, those controls would
  // enable/disable multiple times during the question, which is not what we want.
  const [isThinking, setIsThinking] = useState(false);
  const [isProcessingQuestion, setIsProcessingQuestion] = useState(false);

  const agent = useMemo(
    () =>
      new HttpAgent({
        url: MUSIC_IQ_AGENT_URL,
        threadId: crypto.randomUUID(),
        headers: { Accept: 'text/event-stream' },
      }),
    [],
  );

  // Make sure to clean up the timer on unmount.
  useEffect(() => {
    return () => {
      clearTimeout(thinkingTimeoutRef.current);

      if (agent.isRunning) {
        agent.abortRun();
      }
    };
  }, [agent]);

  const abort = useCallback(() => {
    if (thinkingTimeoutRef.current) {
      clearTimeout(thinkingTimeoutRef.current);
      thinkingTimeoutRef.current = undefined;
    }

    if (agent.isRunning) {
      agent.abortRun();
      setIsThinking(false);
      setIsProcessingQuestion(false);

      setMessages((prev) => [
        ...prev,
        {
          id: `aborted-${Date.now()}`,
          type: 'aborted',
          content: t('chatbot.stoppedResponse'),
        },
      ]);
    }
  }, [t, agent]);

  const ask = useCallback(
    async (question: string) => {
      const userMessageId = `user-${Date.now()}`;
      const agentMessageId = `agent-${Date.now()}`;

      // Add the question as a user message.
      setMessages((prev) => [
        ...prev,
        { id: userMessageId, type: 'user', content: question },
      ]);

      setIsThinking(true);
      setIsProcessingQuestion(true);

      // Add user message in AG-UI format so the agent sends it in the run.
      agent.addMessage({ id: userMessageId, role: 'user', content: question });

      // Add a placeholder message for the agent's response.
      setMessages((prev) => [
        ...prev,
        { id: agentMessageId, type: 'agent', content: '' },
      ]);

      const subscriber: AgentSubscriber = {
        onTextMessageContentEvent({ textMessageBuffer }) {
          setIsThinking(false);

          if (thinkingTimeoutRef.current) {
            clearTimeout(thinkingTimeoutRef.current);
          }

          thinkingTimeoutRef.current = setTimeout(() => {
            setIsThinking(true);
          }, THINKING_TIMEOUT_MS);

          // Replace the contents of the agent's response message with the new content.
          setMessages((prev) =>
            prev.map((msg) => {
              return msg.id === agentMessageId
                ? { ...msg, content: textMessageBuffer }
                : msg;
            }),
          );
        },

        onRunFailed({ error: runError }) {
          // Ignore AbortError because it was intentionally triggered by the user.
          if (runError.name === 'AbortError') {
            return;
          }

          // Add an error message.
          setMessages((prev) => [
            ...prev,
            {
              id: `error-${Date.now()}`,
              type: 'error',
              content: t('chatbot.error'),
            },
          ]);
        },
      };

      try {
        await agent.runAgent({}, subscriber);
      } catch {
        // NOTE: Some weird behaviour here. Even if we handle the error in onRunFailed(),
        // ag-ui still throws an error. So we have to have a catch. But we don't want to
        // add two error messages so we do that only in onRunFailed(). Doing it there just
        // in case there is a scenario where onRunFailed() is called but then doesn't throw.
      } finally {
        if (thinkingTimeoutRef.current) {
          clearTimeout(thinkingTimeoutRef.current);
          thinkingTimeoutRef.current = undefined;
        }

        setIsThinking(false);
        setIsProcessingQuestion(false);
      }
    },
    [agent, t],
  );

  return { messages, ask, abort, isThinking, isProcessingQuestion };
}
